import * as THREE from "three";
import { FORMATION_ORDER, THEMES } from "../config/constants.ts";
import { clamp, lerp } from "../utils/math.ts";
import type { ColourTheme, FormationId, ParticleQuality } from "../utils/types.ts";
import { fillFormation } from "./formations.ts";
import { nextHigher, nextLower, particleCountFor, type QualityTier } from "./QualityManager.ts";
import { particleFragment, particleVertex } from "./shaders.ts";
import {
  BLOOM_RADIUS,
  BLOOM_THRESHOLD,
  CAMERA_FOV,
  CAMERA_Y,
  CAMERA_Z,
  ENERGY_BASE,
  FORMATION_RADIUS,
  IDLE_SPIN,
  PIXEL_RATIO_MAX,
  POINT_SIZE_MULT_MAX,
  POINT_SIZE_MULT_MIN,
  SCALE_BASE,
  TONE_EXPOSURE,
  clampEnergy,
  clampExposure,
  clampScale,
  energyFromExpansion,
  energyFromPalm,
  energyFromPinch,
  scaleFromExpansion,
  scaleFromPalm,
  scaleFromPinch,
  viewportCoverage,
} from "./visualBounds.ts";

export interface UniverseDebugSnapshot {
  cameraZ: number;
  scale: number;
  energy: number;
  pointSize: number;
  maxPointSize: number;
  bloom: number;
  bloomRadius: number;
  bloomThreshold: number;
  exposure: number;
  blackHole: number;
  flash: number;
  formation: FormationId;
  radius: number;
  coverage: number;
}

export class ParticleUniverse {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly canvas: HTMLCanvasElement;
  private group = new THREE.Group();
  private points: THREE.Points | null = null;
  private geometry: THREE.BufferGeometry | null = null;
  private material: THREE.ShaderMaterial | null = null;
  private positions = new Float32Array(0);
  private velocities = new Float32Array(0);
  private targets = new Float32Array(0);
  private bases = new Float32Array(0);
  private colors = new Float32Array(0);
  private sizes = new Float32Array(0);
  private seeds = new Float32Array(0);
  private count = 0;
  private autoLevel: QualityTier = "medium";
  private quality: ParticleQuality = "auto";
  private theme: ColourTheme = "nebula";
  private formation: FormationId = "spiral-galaxy";
  private energy = ENERGY_BASE;
  private targetEnergy = ENERGY_BASE;
  private scale = SCALE_BASE;
  private targetScale = SCALE_BASE;
  private yaw = 0.15;
  private pitch = 0.12;
  private targetYaw = 0.15;
  private targetPitch = 0.12;
  private blackHole = 0;
  private targetBlackHole = 0;
  private flash = 0;
  private shock = 0;
  private reducedMotion = false;
  private flashMesh: THREE.Mesh;
  private shockMesh: THREE.Mesh;
  private holeMesh: THREE.Mesh;
  private discMesh: THREE.Mesh;
  private discMeshOuter: THREE.Mesh;
  private stars: THREE.Points | null = null;
  private lastBlackHole = 0;
  private sizeMult = 1;
  private disposed = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: true,
    });
    this.renderer.setClearColor(0x03050a, 1);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, PIXEL_RATIO_MAX));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.NeutralToneMapping;
    this.renderer.toneMappingExposure = TONE_EXPOSURE;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 0.2, 200);
    this.camera.position.set(0, CAMERA_Y, CAMERA_Z);
    this.scene.add(this.group);

    const flashGeo = new THREE.SphereGeometry(0.85, 20, 20);
    const flashMat = new THREE.MeshBasicMaterial({
      color: 0xb8d4ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: true,
    });
    this.flashMesh = new THREE.Mesh(flashGeo, flashMat);
    this.group.add(this.flashMesh);

    const shockGeo = new THREE.RingGeometry(0.7, 0.82, 48);
    const shockMat = new THREE.MeshBasicMaterial({
      color: 0x00d0e8,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
      toneMapped: true,
    });
    this.shockMesh = new THREE.Mesh(shockGeo, shockMat);
    this.shockMesh.rotation.x = Math.PI / 2;
    this.group.add(this.shockMesh);

    const holeGeo = new THREE.SphereGeometry(0.32, 24, 24);
    const holeMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
    this.holeMesh = new THREE.Mesh(holeGeo, holeMat);
    this.holeMesh.scale.setScalar(0.001);
    this.group.add(this.holeMesh);

    const discGeo = new THREE.RingGeometry(0.42, 1.15, 64);
    const discMat = new THREE.MeshBasicMaterial({
      color: 0x66e0ff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
      toneMapped: true,
    });
    this.discMesh = new THREE.Mesh(discGeo, discMat);
    this.discMesh.rotation.x = Math.PI / 2.4;
    this.group.add(this.discMesh);

    const discOuterGeo = new THREE.RingGeometry(1.2, 2.1, 64);
    const discOuterMat = new THREE.MeshBasicMaterial({
      color: 0xb14dff,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
      toneMapped: true,
    });
    this.discMeshOuter = new THREE.Mesh(discOuterGeo, discOuterMat);
    this.discMeshOuter.rotation.x = Math.PI / 2.4;
    this.group.add(this.discMeshOuter);

    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.addStarfield();
    this.rebuild();
  }

  setQuality(quality: ParticleQuality): void {
    if (this.quality === quality) return;
    this.quality = quality;
    this.rebuild();
  }

  adapt(downgrade: boolean, upgrade: boolean): void {
    if (this.quality !== "auto") return;
    if (downgrade && this.autoLevel !== "low") {
      this.autoLevel = nextLower(this.autoLevel);
      this.rebuild();
    } else if (upgrade && this.autoLevel !== "high") {
      this.autoLevel = nextHigher(this.autoLevel);
      this.rebuild();
    }
  }

  setTheme(theme: ColourTheme): void {
    if (this.theme === theme) return;
    this.theme = theme;
    this.applyColors();
  }

  setFormation(id: FormationId): void {
    this.formation = id;
    fillFormation(id, this.count, this.targets, this.bases);
  }

  cycleFormation(direction = 1): FormationId {
    const index = FORMATION_ORDER.indexOf(this.formation);
    const next =
      FORMATION_ORDER[(index + direction + FORMATION_ORDER.length) % FORMATION_ORDER.length]!;
    this.setFormation(next);
    return next;
  }

  currentFormation(): FormationId {
    return this.formation;
  }

  awaken(): void {
    this.targetEnergy = energyFromPalm();
    this.targetScale = scaleFromPalm();
  }

  compress(amount: number): void {
    this.targetScale = scaleFromPinch(amount);
    this.targetEnergy = energyFromPinch(amount);
  }

  expandCosmic(amount: number): void {
    this.targetScale = scaleFromExpansion(amount);
    this.targetEnergy = energyFromExpansion(amount);
  }

  orbit(nx: number, ny: number): void {
    this.targetYaw = lerp(-0.7, 0.7, nx);
    this.targetPitch = lerp(-0.32, 0.32, 1 - ny);
  }

  setBlackHole(active: boolean): void {
    this.targetBlackHole = active ? 1 : 0;
    if (active) this.targetEnergy = 0.8;
  }

  settleToBaseline(): void {
    this.targetEnergy = ENERGY_BASE;
    this.targetScale = SCALE_BASE;
    this.targetBlackHole = 0;
  }

  supernova(): void {
    this.flash = 1;
    this.shock = 1;
    this.energy = 1;
    this.targetEnergy = ENERGY_BASE;
    for (let i = 0; i < this.count; i += 1) {
      const o = i * 3;
      const x = this.positions[o]!;
      const y = this.positions[o + 1]!;
      const z = this.positions[o + 2]!;
      const len = Math.max(0.001, Math.hypot(x, y, z));
      const impulse = 3.2 + (this.seeds[i] ?? 0.5) * 1.8;
      this.velocities[o] = (x / len) * impulse;
      this.velocities[o + 1] = (y / len) * impulse * 0.55;
      this.velocities[o + 2] = (z / len) * impulse;
    }
  }

  resetUniverse(): void {
    this.energy = ENERGY_BASE;
    this.targetEnergy = ENERGY_BASE;
    this.scale = SCALE_BASE;
    this.targetScale = SCALE_BASE;
    this.yaw = 0.15;
    this.pitch = 0.12;
    this.targetYaw = 0.15;
    this.targetPitch = 0.12;
    this.blackHole = 0;
    this.targetBlackHole = 0;
    this.flash = 0;
    this.shock = 0;
    this.camera.position.set(0, CAMERA_Y, CAMERA_Z);
    this.camera.rotation.set(0, 0, 0);
    this.group.rotation.set(0.12, 0.15, 0);
    this.group.scale.setScalar(SCALE_BASE);
    this.renderer.toneMappingExposure = TONE_EXPOSURE;
    fillFormation(this.formation, this.count, this.targets, this.bases);
    this.positions.set(this.targets);
    this.velocities.fill(0);
    this.applyColors();
  }

  mouseOrbit(nx: number, ny: number): void {
    this.orbit(nx, ny);
  }

  debugSnapshot(): UniverseDebugSnapshot {
    return {
      cameraZ: this.camera.position.z,
      scale: this.scale,
      energy: this.energy,
      pointSize: Number(this.material?.uniforms.uSize?.value ?? 1),
      bloom: 0,
      exposure: this.renderer.toneMappingExposure,
      blackHole: this.blackHole,
      flash: this.flash,
      formation: this.formation,
      maxPointSize: 8,
      bloomRadius: BLOOM_RADIUS,
      bloomThreshold: BLOOM_THRESHOLD,
      radius: FORMATION_RADIUS * this.scale,
      coverage: viewportCoverage(FORMATION_RADIUS * this.scale, this.camera.position.z, CAMERA_FOV),
    };
  }

  applyLook(look: {
    exposure?: number;
    bloomStrength?: number;
    bloomRadius?: number;
    bloomThreshold?: number;
    pointSizeMult?: number;
    cameraZ?: number;
  }): void {
    if (look.exposure !== undefined) {
      this.renderer.toneMappingExposure = clampExposure(look.exposure);
    }
    if (look.pointSizeMult !== undefined) {
      this.sizeMult = clamp(look.pointSizeMult, POINT_SIZE_MULT_MIN, POINT_SIZE_MULT_MAX);
    }
    if (look.cameraZ !== undefined) {
      this.camera.position.z = clamp(look.cameraZ, 11, 18);
      this.camera.updateProjectionMatrix();
    }
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  update(dt: number, time: number): void {
    if (this.disposed) return;
    const motion = this.reducedMotion ? 0.35 : 1;
    const follow = (current: number, target: number, rate: number) =>
      lerp(current, target, 1 - Math.exp(-dt * rate));

    this.energy = clampEnergy(follow(this.energy, this.targetEnergy, 2.4));
    this.scale = clampScale(follow(this.scale, this.targetScale, 2.6));
    this.yaw = follow(this.yaw, this.targetYaw, 2.8);
    this.pitch = follow(this.pitch, this.targetPitch, 2.8);
    this.blackHole = follow(this.blackHole, this.targetBlackHole, 2.6);
    this.flash = Math.max(0, this.flash - dt * 1.4);
    this.shock = Math.max(0, this.shock - dt * 1.1);

    this.group.rotation.y = this.yaw + time * IDLE_SPIN * motion;
    this.group.rotation.x = this.pitch;
    this.group.scale.setScalar(this.scale);

    const pos = this.positions;
    const vel = this.velocities;
    const tgt = this.targets;
    const count = this.count;
    const pull = 1.6 + this.blackHole * 4.2;
    const swirl = this.blackHole * 2.6;
    const reform = 0.9 + (1 - this.blackHole) * 1.6;

    for (let i = 0; i < count; i += 1) {
      const o = i * 3;
      let x = pos[o]!;
      let y = pos[o + 1]!;
      let z = pos[o + 2]!;
      const tx = tgt[o]!;
      const ty = tgt[o + 1]!;
      const tz = tgt[o + 2]!;
      vel[o] = vel[o]! * 0.94 + (tx - x) * reform * dt;
      vel[o + 1] = vel[o + 1]! * 0.94 + (ty - y) * reform * dt;
      vel[o + 2] = vel[o + 2]! * 0.94 + (tz - z) * reform * dt;
      if (this.blackHole > 0.05) {
        const len = Math.max(0.05, Math.hypot(x, y, z));
        vel[o] += ((-x / len) * pull - z * swirl) * dt * this.blackHole;
        vel[o + 1] += -y * 2.2 * dt * this.blackHole;
        vel[o + 2] += ((-z / len) * pull + x * swirl) * dt * this.blackHole;
      }
      x += vel[o]! * dt * motion;
      y += vel[o + 1]! * dt * motion;
      z += vel[o + 2]! * dt * motion;
      pos[o] = x;
      pos[o + 1] = y;
      pos[o + 2] = z;
    }

    const geo = this.geometry;
    if (geo) geo.getAttribute("position").needsUpdate = true;
    if (this.material) {
      this.material.uniforms.uEnergy!.value = this.energy;
      this.material.uniforms.uSize!.value = this.sizeMult;
    }

    const flashMat = this.flashMesh.material as THREE.MeshBasicMaterial;
    flashMat.opacity = this.flash * 0.9;
    this.flashMesh.scale.setScalar(1.1 + (1 - this.flash) * 3.2);

    const shockMat = this.shockMesh.material as THREE.MeshBasicMaterial;
    shockMat.opacity = this.shock * 0.62;
    const shockScale = 0.5 + (1 - this.shock) * 4.5;
    this.shockMesh.scale.set(shockScale, shockScale, shockScale);

    this.holeMesh.scale.setScalar(0.001 + this.blackHole * 0.42);
    const discMat = this.discMesh.material as THREE.MeshBasicMaterial;
    discMat.opacity = this.blackHole * 0.85;
    const discOuterMat = this.discMeshOuter.material as THREE.MeshBasicMaterial;
    discOuterMat.opacity = this.blackHole * 0.45;
    this.discMesh.rotation.z = time * 1.6 * this.blackHole;
    this.discMeshOuter.rotation.z = -time * 0.9 * this.blackHole;
    if (Math.abs(this.blackHole - this.lastBlackHole) > 0.02) {
      this.applyColors();
      this.lastBlackHole = this.blackHole;
    }
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.disposed = true;
    this.geometry?.dispose();
    this.material?.dispose();
    this.flashMesh.geometry.dispose();
    (this.flashMesh.material as THREE.Material).dispose();
    this.shockMesh.geometry.dispose();
    (this.shockMesh.material as THREE.Material).dispose();
    this.holeMesh.geometry.dispose();
    (this.holeMesh.material as THREE.Material).dispose();
    this.discMesh.geometry.dispose();
    (this.discMesh.material as THREE.Material).dispose();
    this.discMeshOuter.geometry.dispose();
    (this.discMeshOuter.material as THREE.Material).dispose();
    this.stars?.geometry.dispose();
    (this.stars?.material as THREE.Material | undefined)?.dispose();
    this.renderer.dispose();
  }

  private addStarfield(): void {
    const count = 1600;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      const r = 22 + Math.random() * 36;
      const phi = Math.acos(2 * Math.random() - 1);
      const theta = Math.random() * Math.PI * 2;
      const o = i * 3;
      positions[o] = r * Math.sin(phi) * Math.cos(theta);
      positions[o + 1] = r * Math.cos(phi);
      positions[o + 2] = r * Math.sin(phi) * Math.sin(theta);
      const c = 0.35 + Math.random() * 0.4;
      colors[o] = c * 0.7;
      colors[o + 1] = c * 0.85;
      colors[o + 2] = c;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const mat = new THREE.PointsMaterial({
      size: 1.4,
      sizeAttenuation: false,
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.NormalBlending,
      toneMapped: true,
    });
    this.stars = new THREE.Points(geo, mat);
    this.scene.add(this.stars);
  }

  private rebuild(): void {
    if (this.points) {
      this.group.remove(this.points);
      this.geometry?.dispose();
      this.material?.dispose();
    }
    this.count = particleCountFor(this.quality, this.autoLevel);
    this.positions = new Float32Array(this.count * 3);
    this.velocities = new Float32Array(this.count * 3);
    this.targets = new Float32Array(this.count * 3);
    this.bases = new Float32Array(this.count * 3);
    this.colors = new Float32Array(this.count * 3);
    this.sizes = new Float32Array(this.count);
    this.seeds = new Float32Array(this.count);
    fillFormation(this.formation, this.count, this.targets, this.bases);
    this.positions.set(this.targets);
    assertFinite(this.positions, "particle positions");
    assertFinite(this.targets, "particle targets");
    for (let i = 0; i < this.count; i += 1) {
      this.seeds[i] = Math.random();
      this.sizes[i] = 1.05 + Math.random() * 0.45;
    }
    this.applyColors();
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute("aColor", new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setAttribute("aSize", new THREE.BufferAttribute(this.sizes, 1));
    this.geometry.computeBoundingSphere();
    this.geometry.computeBoundingBox();
    this.material = new THREE.ShaderMaterial({
      vertexShader: particleVertex,
      fragmentShader: particleFragment,
      uniforms: {
        uEnergy: { value: this.energy },
        uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, PIXEL_RATIO_MAX) },
        uSize: { value: this.sizeMult },
      },
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: true,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.visible = true;
    this.group.visible = true;
    this.group.add(this.points);
  }

  private applyColors(): void {
    const palette = THEMES[this.theme];
    const color = new THREE.Color();
    const holeTint = new THREE.Color("#6a3dff");
    for (let i = 0; i < this.count; i += 1) {
      color.set(palette[i % palette.length]!);
      if (this.blackHole > 0.4) color.lerp(holeTint, 0.35);
      const o = i * 3;
      this.colors[o] = color.r;
      this.colors[o + 1] = color.g;
      this.colors[o + 2] = color.b;
    }
    const attr = this.geometry?.getAttribute("aColor");
    if (attr) attr.needsUpdate = true;
  }
}

export function isWebGLAvailable(canvas: HTMLCanvasElement): boolean {
  try {
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

function assertFinite(buffer: Float32Array, label: string): void {
  for (let i = 0; i < buffer.length; i += 1) {
    const value = buffer[i]!;
    if (!Number.isFinite(value)) {
      throw new Error(`GESTUREVERSE: ${label} contains a non-finite value at index ${i}.`);
    }
  }
}
